import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Loader2, Map, Send, Sparkles, Square, X, Zap } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { embedApi } from '../api/embedApi';
import { writeStoredPass } from '../components/embed/captchaPass';
import { useTurnstile } from '../components/embed/useTurnstile';
import { DynamicChart } from '../components/workspace/Charts';
import InsightBlock from '../components/workspace/InsightBlock';
import PlotContainer from '../components/workspace/PlotContainer';
import { pickThinkingPhrase } from '../components/workspace/thinkingPhrases';

const markdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const MarkdownText = ({ children, inline = false }) => {
  if (!children) return null;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        ...markdownComponents,
        p: ({ children: nodes }) => (inline ? <span>{nodes}</span> : <p>{nodes}</p>),
      }}
    >
      {children}
    </ReactMarkdown>
  );
};

const EmbedMessage = ({ message, onSuggestion, onOpenMap }) => {
  const isBot = message.role === 'assistant';
  const [openingPhrase] = useState(() => pickThinkingPhrase());
  const showPlot = Boolean(message.chartData?.length) && !message.tableOnly && message.spec;
  return (
    <div className="mb-6">
      <div className={`flex w-full ${isBot ? '' : 'justify-end'}`}>
        <div className={isBot
          ? 'w-full text-gray-800'
          : 'max-w-[85%] rounded-2xl rounded-tr-none bg-emerald-50 p-4 text-black shadow-sm'}
        >
          {isBot && message.isLoading && (
            <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
              <Loader2 size={12} className="shrink-0 animate-spin text-gray-400" />
              <span>{message.status || openingPhrase}</span>
            </div>
          )}
          {message.content && (
            <div className={isBot
              ? 'prose prose-base max-w-none prose-gray prose-headings:text-gray-900 prose-p:text-gray-800 prose-a:text-emerald-600 prose-code:bg-gray-100 prose-code:text-emerald-600'
              : 'prose prose-base max-w-none prose-p:text-black prose-headings:text-black'}
            >
              <MarkdownText>{message.content}</MarkdownText>
            </div>
          )}
          {(message.chartData || message.tableOnly) && (
            <PlotContainer
              data={message.chartData}
              title={message.spec?.title}
              rowCount={message.rowCount}
              tableOnly={!!message.tableOnly}
              spec={message.spec}
              isLoading={message.isLoading}
            >
              {showPlot && (
                <DynamicChart data={message.chartData} spec={message.spec} noContainer />
              )}
            </PlotContainer>
          )}
          {message.mapId && (
            <button
              type="button"
              onClick={() => onOpenMap(message)}
              className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-left transition hover:bg-emerald-100"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <Map size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Map</span>
                <span className="block truncate text-sm font-semibold text-gray-900">
                  {message.mapName || 'Open map'}
                </span>
              </span>
              <ExternalLink size={15} className="shrink-0 text-emerald-700" />
            </button>
          )}
          {(message.insightLoading || message.insight?.key_findings?.length > 0) && (
            <InsightBlock insight={message.insight} loading={!!message.insightLoading} />
          )}
        </div>
      </div>
      {isBot && message.suggestions?.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-500">
            <Sparkles size={12} />
            <span>Suggestions</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {message.suggestions.map(question => (
              <button
                key={question}
                type="button"
                onClick={() => onSuggestion(question)}
                className="rounded-md bg-gray-50 px-2.5 py-1 text-left text-xs text-black transition-colors hover:bg-gray-100"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DEFAULT_STARTER_QUESTIONS = [
  'What questions can you answer?',
  'Brief description of the available datasets',
  'How can I visualize my data on a map?',
];

const EmbedChatSurface = ({ publicKey, welcome, questions = [], onClose }) => {
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [modelTier, setModelTier] = useState('deep');
  const [captcha, setCaptcha] = useState(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);
  const sessionRef = useRef(null);
  const siteKey = !session && captcha?.required ? (captcha.site_key || '') : '';
  const { containerRef, acquireToken, error: captchaError, active: captchaActive } = useTurnstile(siteKey);
  const acquireTokenRef = useRef(acquireToken);
  acquireTokenRef.current = acquireToken;

  useEffect(() => {
    let cancelled = false;
    embedApi.getCaptcha()
      .then(data => {
        if (!cancelled) setCaptcha(data);
      })
      .catch(() => {
        if (!cancelled) setError('Captcha is unavailable.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!publicKey) {
      setError('This chat is missing its key.');
      return undefined;
    }
    if (!captcha) return undefined;
    if (captcha.required && !captcha.site_key) {
      setError('Captcha is not configured.');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = captcha.required ? await acquireTokenRef.current() : '';
        if (cancelled || sessionRef.current) return;
        const data = await embedApi.createChat(publicKey, token);
        if (cancelled || sessionRef.current) return;
        sessionRef.current = data;
        setSession(data);
      } catch (err) {
        if (!cancelled && !sessionRef.current) setError(err.message || 'This chat is unavailable.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicKey, captcha]);

  const pinToEndRef = useRef(true);

  const rememberScroll = () => {
    const node = listRef.current;
    if (!node) return;
    pinToEndRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
  };

  useEffect(() => {
    const node = listRef.current;
    if (!node || !pinToEndRef.current) return;
    node.scrollTop = node.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || !session?.chat_id || sending) return;
    pinToEndRef.current = true;
    setSending(true);
    const abort = new AbortController();
    abortRef.current = abort;
    const assistantId = `a-${Date.now()}`;
    setDraft('');
    setMessages(prev => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: trimmed },
      { id: assistantId, role: 'assistant', content: '', isLoading: true },
    ]);
    try {
      await embedApi.streamChat({
        publicKey,
        chatId: session.chat_id,
        text: trimmed,
        captchaPass: session.captcha_pass,
        modelTier,
        abortSignal: abort.signal,
        onEvent: (event) => {
          setMessages(prev => prev.map(message => {
            if (message.id !== assistantId) return message;
            const next = { ...message };
            if (event.type === 'summary') {
              const text = event.content || '';
              if (text && !next.content) {
                next.content = text;
              } else if (text && !next.content.includes(text)) {
                next.content = `${next.content}\n\n${text}`;
              }
              next.streamedText = false;
            } else if (event.type === 'text_delta') {
              next.streamedText = true;
              next.content = event.append === false ? (event.content || '') : `${next.content || ''}${event.content || ''}`;
            } else if (event.type === 'agent_step') {
              if (event.step) next.status = event.step;
              if (event.insight) next.insightLoading = true;
            } else if (event.type === 'result') {
              if (event.visualizationSpec) {
                next.chartData = event.rows;
                next.spec = event.visualizationSpec;
                next.tableOnly = false;
                next.rowCount = event.rows?.length || 0;
              } else if (event.rows) {
                next.chartData = event.rows;
                next.tableOnly = true;
                next.spec = undefined;
                next.rowCount = event.rows.length;
              }
            } else if (event.type === 'map_preview') {
              next.mapId = event.map_id;
              next.mapName = event.name || 'Map';
            } else if (event.type === 'insight') {
              next.insightLoading = false;
              next.insight = { key_findings: event.key_findings || [] };
            } else if (event.type === 'more_questions') {
              next.suggestions = event.questions || [];
            } else if (event.type === 'error') {
              next.content = event.content || 'Something went wrong.';
              next.isLoading = false;
              next.insightLoading = false;
            }
            return next;
          }));
        },
      });
      setMessages(prev => prev.map(message => (
        message.id === assistantId ? { ...message, isLoading: false } : message
      )));
    } catch (err) {
      if (err?.name === 'AbortError') {
        setMessages(prev => prev.map(message => (
          message.id === assistantId ? { ...message, isLoading: false } : message
        )));
        return;
      }
      const notice = err.message || 'Sorry, I could not answer that.';
      setMessages(prev => prev.map(message => (
        message.id === assistantId
          ? { ...message, isLoading: false, content: notice }
          : message
      )));
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const customQuestions = Array.isArray(session?.example_questions)
    ? session.example_questions.map(question => String(question).trim()).filter(Boolean)
    : [];
  const starters = customQuestions.length > 0
    ? customQuestions
    : (questions.length > 0 ? questions : DEFAULT_STARTER_QUESTIONS);
  const openMap = (message) => {
    writeStoredPass(publicKey, session.captcha_pass);
    const params = new URLSearchParams({ key: publicKey, map: message.mapId });
    window.open(`${window.location.origin}/embed/map?${params}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden overscroll-contain bg-white text-gray-900">
      <header className="shrink-0 px-4 py-1.5">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2">
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-600">
            {session?.name || 'Chat'}
          </h3>
          {onClose && (
            <button
              type="button"
              aria-label="Close chat"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </header>
      <div ref={listRef} onScroll={rememberScroll} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
        <div className="mx-auto w-full max-w-2xl">
        {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
        {!error && !session && (
          <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
            <Loader2 size={12} className="shrink-0 animate-spin text-gray-400" />
            <span>{captchaActive ? 'Confirm you are human to start.' : 'Loading...'}</span>
          </div>
        )}
        {session && messages.length === 0 && welcome && (
          <p className="mb-4 whitespace-pre-wrap text-sm text-gray-800">{welcome}</p>
        )}
        {session && messages.length === 0 && starters.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {starters.map(question => (
              <button
                key={question}
                type="button"
                onClick={() => send(question)}
                className="rounded-md bg-gray-50 px-2.5 py-1 text-left text-xs text-black transition-colors hover:bg-gray-100"
              >
                {question}
              </button>
            ))}
          </div>
        )}
        {messages.map(message => (
          <EmbedMessage
            key={message.id}
            message={message}
            onSuggestion={send}
            onOpenMap={openMap}
          />
        ))}
        </div>
      </div>
      {captchaActive && (
        <div className="shrink-0 px-4">
          <div className="mx-auto w-full max-w-2xl">
            <div ref={containerRef} />
            {captchaError && <p className="mt-1 text-xs text-red-600">{captchaError}</p>}
          </div>
        </div>
      )}
      <form
        className="w-full shrink-0 px-4 pb-2 pt-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (sending) return;
          send(draft);
        }}
      >
        <div className="mx-auto w-full max-w-2xl">
        <div className="relative rounded-2xl border border-gray-200 bg-white shadow-xl transition-all focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500">
          <textarea
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (!sending) send(draft);
              }
            }}
            placeholder={sending ? 'Chat is in progress...' : 'Ask anything...'}
            rows={1}
            disabled={!session}
            className="max-h-32 min-h-[52px] w-full resize-none rounded-2xl border-0 bg-transparent p-3 pr-36 text-sm text-gray-900 outline-none disabled:opacity-60"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            <div className="relative inline-flex items-center">
              <span className="pointer-events-none absolute left-2 text-emerald-700/80">
                <Zap size={14} />
              </span>
              <select
                value={modelTier}
                onChange={event => setModelTier(event.target.value)}
                className="h-8 appearance-none rounded-full border-0 bg-emerald-50 pl-7 pr-7 text-xs text-emerald-800 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/25"
                aria-label="Model tier"
              >
                <option value="fast">Fast</option>
                <option value="deep">Deep</option>
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-1.5 text-emerald-700/70" />
            </div>
            {sending ? (
              <button
                type="button"
                onClick={() => abortRef.current?.abort()}
                className="rounded-xl bg-red-600 p-2 text-white shadow-md hover:bg-red-700"
                title="Stop"
                aria-label="Stop"
              >
                <Square size={18} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!session || !draft.trim()}
                className="rounded-xl bg-emerald-600 p-2 text-white shadow-md hover:bg-emerald-700 disabled:opacity-50"
                title="Send"
                aria-label="Send"
              >
                <Send size={18} />
              </button>
            )}
          </div>
        </div>
        <p className="mx-auto mt-2 max-w-2xl text-center text-xs text-gray-400">
          Manteo AI can make mistakes. Check important info.
        </p>
        <a
          href="https://manteo.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="mx-auto mt-1 flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-gray-600"
        >
          Powered by
          <img src="/logo.svg" alt="manteo.ai" className="h-4 w-auto" />
        </a>
        </div>
      </form>
    </div>
  );
};

const EmbedChatPage = () => {
  const [searchParams] = useSearchParams();
  const publicKey = searchParams.get('key') || '';
  const welcome = (searchParams.get('message') || '').trim().slice(0, 500);
  const questions = searchParams.getAll('question')
    .map(question => question.trim().slice(0, 500))
    .filter(Boolean)
    .slice(0, 6);
  const requestClose = () => {
    if (window.parent !== window) {
      window.parent.postMessage({ source: 'manteo-embed', type: 'close' }, '*');
    }
  };

  return (
    <div className="fixed inset-0">
      <EmbedChatSurface publicKey={publicKey} welcome={welcome} questions={questions} onClose={requestClose} />
    </div>
  );
};

export default EmbedChatPage;
